/**
 * UnderstandingOnboarding — V1 (Feb 2026)
 * =========================================
 *
 * Lightweight, role-aware "starting-point understanding" capture for
 * Learners, PDIs, and ADIs. Lives at `/onboarding/understanding`.
 *
 * Doctrine: invite onboarding gets the user through the door;
 * understanding onboarding helps the platform know who has walked
 * in. The result is a LIVING understanding file — the first
 * snapshot is preserved as the starting point; later edits append
 * new versions without overwriting it.
 *
 * The same component renders in two modes:
 *   • CREATE  — first snapshot (no `current` yet)
 *   • EDIT    — prefilled from `current`, appends a new version
 *
 * Authority for question slugs lives in
 * `backend/understanding.py::{LEARNER,PDI,ADI}_QUESTION_SLUGS`.
 * Pinned by `backend/tests/test_understanding_doctrine.py`.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { Check, ArrowRight, ArrowLeft, Heart } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ────────────────────────────────────────────────────────────────
// Role-aware question schemas. The `slug` MUST exactly match the
// canonical backend frozensets. The doctrine pin enforces this.
// ────────────────────────────────────────────────────────────────

const SCHEMAS = {
  learner: [
    {
      slug: "goals",
      title: "What would driving help you achieve?",
      sub: "Pick anything that matters to you — no wrong answers.",
      type: "chips",
      options: ["Freedom", "Work", "Family", "Independence", "Confidence", "Travel", "Other"],
    },
    {
      slug: "learning_preferences",
      title: "How do you usually understand things best?",
      sub: "How you learn matters — pick what feels true for you.",
      type: "chips",
      options: ["Visual examples", "Step-by-step explanations", "Discussion", "Practical experience", "Reading", "Repetition"],
    },
    {
      slug: "success_meaning",
      title: "What would make lessons feel successful?",
      sub: "Define what success looks like in your own words.",
      type: "chips",
      options: ["Understanding more", "Feeling calmer", "Making fewer mistakes", "Becoming more independent", "Passing my test"],
    },
    {
      slug: "optional_reflection",
      title: "Anything you'd like your instructor to know?",
      sub: "Optional. Skip if nothing comes to mind.",
      type: "text",
      placeholder: "Take your time — there's no pressure here.",
    },
  ],
  pdi: [
    {
      slug: "trainee_confidence",
      title: "How confident do you feel as a trainee right now?",
      sub: "Honest is best. This is a starting point, not a verdict.",
      type: "slider",
      min: 1, max: 5,
      labels: ["Very low", "Low", "Mixed", "Good", "Strong"],
    },
    {
      slug: "pressure_areas",
      title: "Where do you feel the most pressure?",
      sub: "Knowing this helps your supervisor support you better.",
      type: "chips",
      options: ["Standards Check", "Part 3", "Pupil management", "Lesson planning", "Time", "Feedback", "Observation"],
    },
    {
      slug: "uncertainty_areas",
      title: "Which areas feel unclear right now?",
      sub: "Not weaknesses — just things still settling.",
      type: "chips",
      options: ["Fault assessment", "Risk management", "Teaching technique", "Client-centred learning", "Reflection", "Lesson structure"],
    },
    {
      slug: "learning_preferences",
      title: "How do you learn best?",
      sub: "Pick anything that resonates.",
      type: "chips",
      options: ["Watching others", "Step-by-step", "Discussion", "Practice", "Reading", "Repetition"],
    },
    {
      slug: "training_goals",
      title: "What are you working towards right now?",
      sub: "Doesn't have to be one thing.",
      type: "chips",
      options: ["Standards Check pass", "Part 3 pass", "Confident pupil delivery", "Stable income", "Independence", "Reputation"],
    },
    {
      slug: "self_belief",
      title: "How much do you believe you can do this?",
      sub: "Just where you are today.",
      type: "slider",
      min: 1, max: 5,
      labels: ["Not yet", "Working on it", "Mixed", "Mostly yes", "Strongly yes"],
    },
    {
      slug: "observation_confidence",
      title: "How does being observed feel?",
      sub: "Useful context for your supervisor and ECO sessions.",
      type: "slider",
      min: 1, max: 5,
      labels: ["Very nervous", "Nervous", "Mixed", "Okay", "Comfortable"],
    },
    {
      slug: "standards_check_concerns",
      title: "Anything about Standards Check that worries you?",
      sub: "Naming concerns is half the work.",
      type: "chips",
      options: ["Examiner pressure", "Fault assessment", "Time keeping", "Lesson structure", "Specific pupil types", "Nothing in particular"],
    },
    {
      slug: "optional_reflection",
      title: "Anything else you'd like us to know?",
      sub: "Optional. Skip if nothing comes to mind.",
      type: "text",
      placeholder: "A thought, a worry, a hope — whatever feels right.",
    },
  ],
  adi: [
    {
      slug: "teaching_goals",
      title: "What kind of instructor do you want to be?",
      sub: "Your teaching purpose, in your own terms.",
      type: "chips",
      options: ["Confidence-led", "Safety-focused", "Test-result driven", "Lifelong skills", "Calm + patient", "Adaptive"],
    },
    {
      slug: "business_goals",
      title: "What are you building business-wise?",
      sub: "Realistic is fine. We meet you where you are.",
      type: "chips",
      options: ["Steady income", "Full diary", "Multiple cars", "Driving school", "Marketplace listing", "Part-time + balance"],
    },
    {
      slug: "confidence_areas",
      title: "What do you feel strong at?",
      sub: "Strengths are evidence too.",
      type: "chips",
      options: ["Beginners", "Test prep", "Anxious pupils", "Manoeuvres", "Theory", "Communication", "Patience"],
    },
    {
      slug: "development_areas",
      title: "Where would you like to grow?",
      sub: "No judgement — just intent.",
      type: "chips",
      options: ["Difficult pupils", "Time management", "Pricing", "Online presence", "Reflection", "Feedback", "Tech use"],
    },
    {
      slug: "learning_preferences",
      title: "How do you learn best?",
      sub: "We'll match content + support to this.",
      type: "chips",
      options: ["Videos", "Reading", "Discussion", "Practice", "Coaching", "Peer review"],
    },
    {
      slug: "support_areas",
      title: "Where would platform support be most useful?",
      sub: "Tells us what to prioritise for you.",
      type: "chips",
      options: ["Diary", "Payments", "Pupil progression", "Marketing", "Trainee management", "Lesson notes", "Reflection"],
    },
    {
      slug: "optional_reflection",
      title: "Anything else you'd like us to know?",
      sub: "Optional. Skip if nothing comes to mind.",
      type: "text",
      placeholder: "We're listening.",
    },
  ],
};

// ────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────

export default function UnderstandingOnboarding() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isEditMode = location.pathname.endsWith("/edit") || new URLSearchParams(location.search).get("edit") === "1";

  const [role, setRole] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [stepIdx, setStepIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasStartingPoint, setHasStartingPoint] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/understanding/me`, { withCredentials: true });
        if (!alive) return;
        const r = data?.role;
        if (!r || !SCHEMAS[r]) {
          toast.error("Understanding files are not enabled for your role.");
          nav("/", { replace: true });
          return;
        }
        setRole(r);
        setQuestions(SCHEMAS[r]);
        setHasStartingPoint(!!data.has_starting_point);
        if (data.current?.snapshot) {
          setAnswers(data.current.snapshot);
        }
      } catch (err) {
        toast.error("Could not load your understanding file. Please try again.");
        nav("/", { replace: true });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [nav]);

  const progress = useMemo(
    () => (questions.length ? Math.round(((stepIdx + 1) / questions.length) * 100) : 0),
    [stepIdx, questions.length]
  );

  const current = questions[stepIdx];

  const setAnswer = (slug, value) => setAnswers((a) => ({ ...a, [slug]: value }));

  const toggleChip = (slug, option) => {
    setAnswers((a) => {
      const list = Array.isArray(a[slug]) ? a[slug] : [];
      return {
        ...a,
        [slug]: list.includes(option) ? list.filter((x) => x !== option) : [...list, option],
      };
    });
  };

  const goNext = () => {
    if (stepIdx < questions.length - 1) setStepIdx((i) => i + 1);
  };
  const goBack = () => {
    if (stepIdx > 0) setStepIdx((i) => i - 1);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/understanding/snapshot`,
        { snapshot: answers },
        { withCredentials: true }
      );
      toast.success(
        hasStartingPoint
          ? "Understanding file updated — your starting point is preserved."
          : "Thank you. Your starting point is saved."
      );
      // Refresh user so any role-derived UI updates pick up the
      // understanding_complete state (future-proof — we don't write
      // that flag yet, but we don't want stale auth either).
      await refreshUser?.();
      const home = role === "learner" ? "/learn" : role === "pdi" ? "/pdi" : "/work";
      nav(home, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 text-sm">Loading…</div>
      </div>
    );
  }

  if (!current) return null;

  const isLast = stepIdx === questions.length - 1;
  const isOptional = current.slug === "optional_reflection";
  const hasAnswer = (() => {
    const v = answers[current.slug];
    if (current.type === "text") return true; // always optional
    if (current.type === "chips") return Array.isArray(v) && v.length > 0;
    if (current.type === "slider") return typeof v === "number";
    return false;
  })();

  return (
    <div
      data-testid="understanding-onboarding-page"
      className="min-h-dvh bg-gradient-to-b from-slate-50 to-white px-4 py-8"
    >
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 text-emerald-600 mb-2">
            <Heart className="w-4 h-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">
              {hasStartingPoint ? "Update your understanding" : "Tell us a bit about you"}
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
            {hasStartingPoint ? "Living understanding file" : "Starting-point understanding"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {hasStartingPoint
              ? "Your starting point is preserved. This update will be added as a new version."
              : "A few light questions so we know how to support you. Skip anything that doesn't feel right."}
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="h-1 w-full bg-slate-200 rounded-full overflow-hidden">
            <div
              data-testid="understanding-progress-bar"
              className="h-full bg-emerald-500 transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between mt-2 text-[11px] text-slate-400">
            <span>Step {stepIdx + 1} of {questions.length}</span>
            <span>{progress}%</span>
          </div>
        </div>

        {/* Question card */}
        <div
          data-testid={`understanding-question-${current.slug}`}
          className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
        >
          <h2 className="text-lg font-bold text-slate-900">{current.title}</h2>
          {current.sub && <p className="text-sm text-slate-500 mt-1">{current.sub}</p>}

          <div className="mt-5">
            {current.type === "chips" && (
              <div className="flex flex-wrap gap-2">
                {current.options.map((opt) => {
                  const selected = Array.isArray(answers[current.slug]) && answers[current.slug].includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`understanding-chip-${current.slug}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => toggleChip(current.slug, opt)}
                      className={
                        "rounded-full px-4 py-2 text-sm border transition-all " +
                        (selected
                          ? "bg-emerald-500 text-white border-emerald-500 shadow-sm"
                          : "bg-white text-slate-700 border-slate-300 hover:border-emerald-400 hover:bg-emerald-50")
                      }
                    >
                      {selected && <Check className="w-3.5 h-3.5 inline mr-1" />}
                      {opt}
                    </button>
                  );
                })}
              </div>
            )}

            {current.type === "slider" && (
              <div>
                <div className="px-2">
                  <Slider
                    data-testid={`understanding-slider-${current.slug}`}
                    min={current.min}
                    max={current.max}
                    step={1}
                    value={[typeof answers[current.slug] === "number" ? answers[current.slug] : current.min]}
                    onValueChange={(v) => setAnswer(current.slug, v[0])}
                  />
                </div>
                <div className="flex justify-between mt-3 text-[11px] text-slate-400">
                  {current.labels?.map((l) => <span key={l}>{l}</span>)}
                </div>
                {typeof answers[current.slug] === "number" && current.labels && (
                  <div className="text-center mt-3 text-sm font-semibold text-emerald-600">
                    {current.labels[answers[current.slug] - current.min]}
                  </div>
                )}
              </div>
            )}

            {current.type === "text" && (
              <Textarea
                data-testid={`understanding-text-${current.slug}`}
                value={answers[current.slug] || ""}
                onChange={(e) => setAnswer(current.slug, e.target.value)}
                placeholder={current.placeholder || ""}
                rows={4}
                className="resize-none"
              />
            )}
          </div>
        </div>

        {/* Nav */}
        <div className="flex items-center justify-between mt-6">
          <Button
            data-testid="understanding-back-btn"
            type="button"
            variant="ghost"
            onClick={goBack}
            disabled={stepIdx === 0 || submitting}
            className="text-slate-500"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>

          {!isLast ? (
            <Button
              data-testid="understanding-next-btn"
              type="button"
              onClick={goNext}
              disabled={!hasAnswer && !isOptional}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              Continue
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              data-testid="understanding-submit-btn"
              type="button"
              onClick={submit}
              disabled={submitting}
              className="bg-emerald-500 hover:bg-emerald-600"
            >
              {submitting ? "Saving…" : hasStartingPoint ? "Save update" : "Save my starting point"}
              <Check className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>

        {/* Skip everything (calm escape hatch) */}
        {!isEditMode && !hasStartingPoint && (
          <div className="text-center mt-6">
            <button
              data-testid="understanding-skip-link"
              type="button"
              onClick={() => {
                const home = role === "learner" ? "/learn" : role === "pdi" ? "/pdi" : "/work";
                nav(home, { replace: true });
              }}
              className="text-xs text-slate-400 hover:text-slate-600 underline"
            >
              I'd rather skip this for now
            </button>
            <p className="text-[10px] text-slate-400 mt-1">
              You can come back any time from your profile.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
